import { useEffect, useState } from "react";
import { useUI } from "@/store/ui";
import { useUpdateListing } from "@/hooks/useListings";
import { formatCHF, formatRooms, formatSurface } from "@/lib/format";
import { STATUSES, type Listing } from "@/types";

export function ListingDetail({ listing }: { listing: Listing }) {
  const close = useUI((s) => () => s.select(null));
  const update = useUpdateListing();
  const [notes, setNotes] = useState(listing.notes);
  const [viewingDate, setViewingDate] = useState<string>(
    listing.viewingDate ? listing.viewingDate.slice(0, 10) : "",
  );

  // Sync local form state when the underlying listing changes (e.g. after a refetch).
  useEffect(() => {
    setNotes(listing.notes);
    setViewingDate(listing.viewingDate ? listing.viewingDate.slice(0, 10) : "");
  }, [listing.id, listing.notes, listing.viewingDate]);

  // ESC closes
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && close();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [close]);

  function saveNotes() {
    if (notes !== listing.notes) {
      update.mutate({ id: listing.id, patch: { notes } });
    }
  }

  function saveViewing() {
    const next = viewingDate ? `${viewingDate}T18:00:00Z` : "";
    const current = listing.viewingDate ?? "";
    if (next !== current) {
      update.mutate({
        id: listing.id,
        patch: { viewingDate: viewingDate ? next : null },
      });
    }
  }

  function setStatus(status: Listing["status"]) {
    update.mutate({ id: listing.id, patch: { status } });
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-ink-900/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={close}
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="sticky top-0 bg-white border-b border-ink-200 px-5 py-3 flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold leading-tight">
              {formatCHF(listing.priceCHF)} · {formatRooms(listing.rooms)} rm ·{" "}
              {formatSurface(listing.surfaceM2)}
            </h2>
            <div className="text-sm text-ink-600 mt-0.5">
              {listing.street ? `${listing.street}, ` : ""}
              {listing.zipcode} {listing.city}
            </div>
          </div>
          <button
            onClick={close}
            className="text-ink-400 hover:text-ink-700 px-2"
            aria-label="Close"
          >
            ✕
          </button>
        </header>

        <div className="p-5 space-y-5">
          {(listing.coverImageUrl || listing.imageUrls.length > 0) && (
            <div className="grid grid-cols-2 gap-2">
              {[listing.coverImageUrl, ...listing.imageUrls]
                .filter(Boolean)
                .slice(0, 6)
                .map((src, i) => (
                  <img
                    key={i}
                    src={src}
                    alt=""
                    loading="lazy"
                    className="w-full h-40 object-cover rounded-md bg-ink-100"
                  />
                ))}
            </div>
          )}

          <div className="flex flex-wrap gap-2 text-xs">
            {listing.attributes.map((a) => (
              <span
                key={a}
                className="px-2 py-0.5 rounded-full bg-ink-100 text-ink-700"
              >
                {a}
              </span>
            ))}
          </div>

          {listing.description && (
            <p className="text-sm text-ink-700 whitespace-pre-line leading-relaxed">
              {listing.description}
            </p>
          )}

          <div className="grid grid-cols-2 gap-4 text-sm">
            <Field label="Available">{listing.movingDate || "—"}</Field>
            <Field label="Agency">{listing.agencyName || "—"}</Field>
            <Field label="Source">
              <a
                href={listing.url}
                target="_blank"
                rel="noreferrer"
                className="text-ink-700 underline hover:text-ink-900"
              >
                Open on {listing.source}
              </a>
            </Field>
            <Field label="First seen">
              {new Date(listing.firstSeen).toLocaleString("de-CH")}
            </Field>
          </div>

          <hr className="border-ink-200" />

          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-ink-600 block mb-1">
                Status
              </label>
              <div className="flex flex-wrap gap-1.5">
                {STATUSES.map((s) => (
                  <button
                    key={s}
                    onClick={() => setStatus(s)}
                    className={
                      listing.status === s
                        ? "px-3 py-1 text-sm rounded-md bg-ink-800 text-white"
                        : "px-3 py-1 text-sm rounded-md bg-ink-100 text-ink-700 hover:bg-ink-200"
                    }
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-xs font-medium text-ink-600 block mb-1">
                Viewing date
              </label>
              <input
                type="date"
                value={viewingDate}
                onChange={(e) => setViewingDate(e.target.value)}
                onBlur={saveViewing}
                className="px-2 py-1 rounded-md border border-ink-300 text-sm"
              />
            </div>

            <div>
              <label className="text-xs font-medium text-ink-600 block mb-1">
                Notes
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                onBlur={saveNotes}
                rows={4}
                placeholder="impressions, follow-ups, agent contact…"
                className="w-full px-3 py-2 rounded-md border border-ink-300 text-sm"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-ink-500">
        {label}
      </div>
      <div className="text-ink-800">{children}</div>
    </div>
  );
}
